import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, IndianRupee, Percent } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Field, Modal } from "./AdminRestaurants";

const EMPTY = { name: "", amount: 0, goes_to: "ADMIN", enabled: true, allow_restaurant_override: false, taxable: false, gst_percent: 5 };

export default function AdminCharges() {
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/admin/additional-charges").then(({ data }) => setRows(data));
    useEffect(() => { load(); }, []);

    const openCreate = () => { setEdit(null); setForm(EMPTY); setOpen(true); };
    const openEdit = (c) => { setEdit(c); setForm({ ...c }); setOpen(true); };

    const save = async () => {
        if (!form.name.trim()) return;
        setBusy(true);
        try {
            const payload = {
                ...form,
                amount: Number(form.amount),
                gst_percent: form.taxable ? (form.gst_percent === "" || form.gst_percent == null ? null : Number(form.gst_percent)) : null,
            };
            if (edit) await api.patch(`/admin/additional-charges/${edit.id}`, payload);
            else await api.post(`/admin/additional-charges`, payload);
            toast.success("Saved"); setOpen(false); load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
    };
    const remove = async (id) => {
        if (!confirm("Delete this charge? Past orders that applied it will keep their reference.")) return;
        try {
            const { data } = await api.delete(`/admin/additional-charges/${id}`);
            toast.success(data?.mode === "archived" ? `Archived — ${data.orders} past order(s) kept` : "Deleted");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const toggle = async (c, key) => {
        await api.patch(`/admin/additional-charges/${c.id}`, { [key]: !c[key] }); load();
    };

    return (
        <div>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold">Additional Charges</h1>
                    <p className="text-sm text-muted-foreground mt-1">Custom line items added to the customer bill. Can be overridden per restaurant when allowed.</p>
                </div>
                <button data-testid="add-charge" onClick={openCreate} className="bg-primary text-primary-foreground rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2"><Plus size={16} /> New charge</button>
            </div>

            <div className="mt-6 bg-card border border-border rounded-2xl divide-y divide-border">
                {rows.map((c) => (
                    <div key={c.id} data-testid={`charge-${c.id}`} className="flex items-center gap-3 p-4">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-bold truncate">{c.name}</p>
                                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${c.goes_to === "ADMIN" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>To {c.goes_to === "ADMIN" ? "Bizil" : "Shop"}</span>
                                {c.taxable && <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-violet-100 text-violet-800">Taxable</span>}
                                {c.allow_restaurant_override && <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-800">Override allowed</span>}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">₹{c.amount}</p>
                        </div>
                        <label className="inline-flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={!!c.enabled} onChange={() => toggle(c, "enabled")} data-testid={`charge-toggle-${c.id}`} className="accent-primary w-4 h-4" />
                            <span className="font-semibold">{c.enabled ? "ON" : "OFF"}</span>
                        </label>
                        <button onClick={() => openEdit(c)} className="p-2 rounded-lg hover:bg-muted"><Pencil size={16} /></button>
                        <button onClick={() => remove(c.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 size={16} /></button>
                    </div>
                ))}
                {rows.length === 0 && <div className="p-8 text-sm text-muted-foreground text-center">No additional charges yet.</div>}
            </div>

            {open && (
                <Modal onClose={() => setOpen(false)} title={edit ? "Edit charge" : "New charge"}>
                    <div className="grid grid-cols-2 gap-3">
                        <Field full label="Name"><input data-testid="charge-name" className="inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Service Fee, Convenience Fee…" /></Field>
                        <Field label="Amount ₹"><input data-testid="charge-amount" type="number" className="inp" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
                        <Field label="Goes to">
                            <div className="flex rounded-xl border border-border overflow-hidden">
                                <button type="button" onClick={() => setForm({ ...form, goes_to: "ADMIN" })} data-testid="goes-admin"
                                    className={`flex-1 px-3 py-2 text-xs font-semibold ${form.goes_to === "ADMIN" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>Bizil (admin)</button>
                                <button type="button" onClick={() => setForm({ ...form, goes_to: "RESTAURANT" })} data-testid="goes-rest"
                                    className={`flex-1 px-3 py-2 text-xs font-semibold ${form.goes_to === "RESTAURANT" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>Shop</button>
                            </div>
                        </Field>
                        <Field full label="">
                            <div className="space-y-2 mt-1">
                                <Check label="Enabled — apply to customer bill" checked={!!form.enabled} onChange={(v) => setForm({ ...form, enabled: v })} />
                                <Check label="Allow override per shop" checked={!!form.allow_restaurant_override} onChange={(v) => setForm({ ...form, allow_restaurant_override: v })} />
                                <Check label="Apply GST on this charge" checked={!!form.taxable} onChange={(v) => setForm({ ...form, taxable: v })} />
                                {form.taxable && (
                                    <div className="ml-6 flex items-center gap-2">
                                        <span className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">GST %</span>
                                        <input
                                            data-testid="charge-gst-pct"
                                            type="number" step="0.01" min="0" max="100"
                                            className="inp max-w-[120px]"
                                            value={form.gst_percent ?? ""}
                                            onChange={(e) => setForm({ ...form, gst_percent: e.target.value })}
                                            placeholder="e.g. 5"
                                        />
                                        <span className="text-xs text-muted-foreground">added to &quot;GST and Other Charges&quot;</span>
                                    </div>
                                )}
                            </div>
                        </Field>
                    </div>
                    <button data-testid="charge-save" disabled={busy || !form.name.trim()} onClick={save}
                        className="mt-4 w-full bg-primary text-primary-foreground rounded-xl py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                        {busy && <Loader2 size={16} className="animate-spin" />} Save
                    </button>
                </Modal>
            )}
        </div>
    );
}

function Check({ label, checked, onChange }) {
    return (
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" className="accent-primary w-4 h-4" checked={checked} onChange={(e) => onChange(e.target.checked)} />
            <span>{label}</span>
        </label>
    );
}
